/**
 * WO-D2.M — Historical import: the SINGLE upload spot. Pick any number of files (receipts, PDFs,
 * Home Depot / Lowe's / eBay CSV exports), upload once, then one-tap bulk-approve. All the
 * extraction/seeding complexity is server-side. No @smartbox/* (invariant 1).
 *
 * WO-M0 — rebuilt on the design system: Screen / Card / Button / Text.
 */
import { useState } from 'react';
import { ActivityIndicator, Alert, StyleSheet, View } from 'react-native';
import { startImport, getImportStatus, approveImport } from '../../src/api/onboarding';
import { pickDocuments, type AcquiredFile } from '../../src/platform/fileCapture';
import { useFileDrop } from '../../src/hooks/useFileDrop';
import { ApiRequestError } from '../../src/api/client';
import { Screen, Text, Card, Button } from '../../src/components/ui';
import { theme } from '../../src/theme';
import type { ImportUploadFile } from '@beam/api-types';

type Phase = 'idle' | 'uploading' | 'review' | 'approving' | 'done';

export default function ImportScreen() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [batchId, setBatchId] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const [result, setResult] = useState<{ confirmed: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function uploadAcquired(acquired: AcquiredFile[]) {
    if (!acquired.length) return;
    setError(null);
    setPhase('uploading');
    try {
      const files: ImportUploadFile[] = acquired.map((a) => ({
        filename: a.filename,
        mimeType: a.mimeType,
        contentBase64: a.contentBase64,
      }));
      const res = await startImport(files);
      setBatchId(res.importBatchId);
      setPending(res.pending);
      setPhase('review');
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Upload failed.');
      setPhase('idle');
    }
  }

  async function pickAndUpload() {
    setError(null);
    const acquired = await pickDocuments();
    await uploadAcquired(acquired);
  }

  // Web (W5) — accept files dropped anywhere while idle or reviewing (add-more).
  useFileDrop((files) => void uploadAcquired(files), phase === 'idle' || phase === 'review');

  async function refresh() {
    if (!batchId) return;
    try {
      const st = await getImportStatus(batchId);
      setPending(st.fact_count - 0);
    } catch { /* ignore */ }
  }

  async function approveAll() {
    if (!batchId) return;
    setPhase('approving');
    setError(null);
    try {
      const res = await approveImport(batchId);
      setResult({ confirmed: res.confirmed, skipped: res.skipped });
      setPhase('done');
      if (res.skipped > 0) {
        Alert.alert(
          'Some items need review',
          `${res.confirmed} approved. ${res.skipped} lower-confidence items are in your review queue.`,
        );
      }
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Approval failed.');
      setPhase('review');
    }
  }

  return (
    <Screen scroll>
      <Text variant="title">Import your history</Text>
      <Text variant="body" color="secondary">
        Add receipts, invoices, and purchase-history exports. We read each file, extract the line
        items, and use them to inform your estimating and forecasting.
      </Text>

      {phase === 'idle' && (
        <Button
          title="Choose files to import"
          variant="primary"
          size="lg"
          onPress={pickAndUpload}
          accessibilityLabel="Choose files to import"
        />
      )}

      {phase === 'uploading' && (
        <View style={styles.center}>
          <ActivityIndicator size="large" accessibilityLabel="Uploading files" />
          <Text variant="body" color="secondary">Uploading & reading your files…</Text>
        </View>
      )}

      {phase === 'review' && (
        <Card>
          <Text variant="label" color="secondary">Ready to file</Text>
          <Text variant="data">{pending} items extracted</Text>
          <Text variant="body" color="secondary">
            Approve everything we're confident about. Anything unclear goes to your review queue.
          </Text>
          <Button
            title="Approve all & file"
            variant="primary"
            size="lg"
            onPress={approveAll}
            accessibilityLabel="Approve all extracted items"
          />
          <Button
            title="Refresh"
            variant="secondary"
            size="md"
            onPress={refresh}
            accessibilityLabel="Refresh item count"
          />
          <Button
            title="Add more files"
            variant="ghost"
            size="md"
            onPress={pickAndUpload}
            accessibilityLabel="Add more files"
          />
        </Card>
      )}

      {phase === 'approving' && (
        <View style={styles.center}>
          <ActivityIndicator size="large" accessibilityLabel="Filing history" />
          <Text variant="body" color="secondary">Filing your history & updating your catalog…</Text>
        </View>
      )}

      {phase === 'done' && result && (
        <Card>
          <Text variant="label" color="secondary">Import complete</Text>
          <Text variant="data">{result.confirmed} items filed</Text>
          <Text variant="body" color="secondary">
            {result.confirmed} items filed as historical
            {result.skipped ? `, ${result.skipped} sent to review` : ''}.
            {' '}Your material costs are now informed by real purchase history.
          </Text>
          <Button
            title="Import more"
            variant="secondary"
            size="md"
            onPress={() => { setPhase('idle'); setResult(null); setBatchId(null); }}
            accessibilityLabel="Import more files"
          />
        </Card>
      )}

      {error ? (
        <Text variant="small" color="danger" accessibilityRole="alert">
          {error}
        </Text>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { alignItems: 'center', gap: theme.spacing.md, paddingVertical: theme.spacing['2xl'] },
});
